import React, { useState, useEffect } from 'react'
import { Database, RefreshCw, Download, Clock, MapPin } from 'lucide-react'
import { isWithinInterval, subHours } from 'date-fns'
import { apiService } from '../services/apiService'
import { geoService } from '../services/geoService'
import WorldMap from './WorldMap'

const DataViewer = () => {
  const [allData, setAllData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [locations, setLocations] = useState({})
  const [loading, setLoading] = useState(true)
  const [loadingGeo, setLoadingGeo] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiService.getData(200) // Get more data
      const data = response.data || []
      setAllData(data)
      
      // Filter data from the last hour
      const oneHourAgo = subHours(new Date(), 1)
      const recentData = data.filter(item => {
        const timestamp = new Date(item.timestamp)
        return isWithinInterval(timestamp, { start: oneHourAgo, end: new Date() })
      })
      
      setFilteredData(recentData)
      
      // Load geolocations for recent IPs
      if (recentData.length > 0) {
        loadGeolocation(recentData)
      }
    } catch (err) {
      setError('Loading error: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const loadGeolocation = async (data) => {
    setLoadingGeo(true)
    try {
      const uniqueIPs = [...new Set(data.map(item => item.client_ip))]
      const geoLocations = await geoService.getLocationsFromIPs(uniqueIPs)
      setLocations(geoLocations)
    } catch (error) {
      console.error('Geolocation error:', error)
    } finally {
      setLoadingGeo(false)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US')
  }

  const getTimeAgo = (dateString) => {
    const now = new Date()
    const timestamp = new Date(dateString)
    const diffMs = now - timestamp
    const diffMins = Math.floor(diffMs / (1000 * 60))
    
    if (diffMins < 1) return "now"
    if (diffMins < 60) return `${diffMins}min`
    return `${Math.floor(diffMins / 60)}h${diffMins % 60}min`
  }

  const exportCSV = () => {
    const csvContent = [
      'IP,Version,Timestamp,City,Country',
      ...filteredData.map(item => {
        const location = locations[item.client_ip]
        return `${item.client_ip},${item.json_data?.version || 'N/A'},${item.timestamp},${location?.city || 'N/A'},${location?.country || 'N/A'}`
      })
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `clayer-versions-lastHour-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Keep only the latest entry per IP in the last hour
  const latestByIP = {};
  filteredData.forEach(item => {
    const ip = item.client_ip;
    const ts = new Date(item.timestamp).getTime();
    if (!latestByIP[ip] || ts > new Date(latestByIP[ip].timestamp).getTime()) {
      latestByIP[ip] = item;
    }
  });
  const uniqueData = Object.values(latestByIP);

  if (loading) {
    return (
      <div className="loading">
        <RefreshCw className="animate-spin" size={24} />
        <span className="ml-2">Loading data...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card">
        <div className="alert alert-error">
          {error}
        </div>
        <button onClick={loadData} className="btn btn-primary mt-4">
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      {/* <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="text-blue-500" size={24} />
            <div>
              <h2 className="text-xl font-semibold">Active C-Layer Nodes</h2>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={exportCSV}
              className="btn btn-secondary"
              disabled={filteredData.length === 0}
            >
              <Download size={16} />
              Export CSV
            </button>
            <button onClick={loadData} className="btn btn-primary">
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>
      </div> */}

      {/* World Map */}
      {filteredData.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <MapPin className="text-green-500" size={20} />
            <h3 className="font-semibold">World Map - Active C-Layer Nodes</h3>
            {loadingGeo && (
              <span className="text-sm text-gray-500">Loading geolocation...</span>
            )}
          </div>
          <WorldMap data={filteredData} locations={locations} />
        </div>
      )}

      {/* Recent versions table */}
      {/* <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <Database className="text-blue-500" size={20} />
          <h3 className="font-semibold">Latest node pings</h3>
        </div>

        {filteredData.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Clock size={48} className="mx-auto mb-4 opacity-50" />
            <p>No versions received in the last hour</p>
            <p className="text-sm mt-2">Total: {allData.length} versions recorded</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>IP Address</th>
                  <th>Version</th>
                  <th>Time ago</th>
                  <th>Timestamp</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                {uniqueData.map((item) => {
                  const location = locations[item.client_ip]
                  return (
                    <tr key={item.id}>
                      <td className="font-mono">{item.client_ip}</td>
                      <td className="font-semibold text-blue-600">
                        {item.json_data?.version || 'N/A'}
                      </td>
                      <td className="text-sm font-medium text-green-600">
                        {getTimeAgo(item.timestamp)}
                      </td>
                      <td className="text-sm text-gray-600">
                        {formatDate(item.timestamp)}
                      </td>
                      <td className="text-sm">
                        {location ? (
                          <div className="flex items-center gap-1">
                            <MapPin size={14} className="text-gray-400" />
                            {location.city}, {location.country}
                          </div>
                        ) : loadingGeo ? (
                          <span className="text-gray-400">Loading...</span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div> */}
    </div>
  )
}

export default DataViewer 